import React, { useEffect, useMemo, useRef, useState } from 'react';
import PassportCardArtwork from '../PassportCard';
import { AdaptiveWorkspaceDialog } from '../../ui/AdaptiveWorkspace';
import { cardEvidenceLabel, type PassportCard } from '../../../services/api/passportCards';
import { collectionRequest, type CollectionResult } from './collectionState';
import { timelineCollectedLabel } from './passportTimelineUtils';

/** Read-only view state. Neither comparison nor folding writes collection layout. */
export function CollectorsDesk({ items: initialItems, query, initialCard, onClose }: {
  items: PassportCard[]; query: string; initialCard?: PassportCard | null; onClose: () => void;
}) {
  const [items, setItems] = useState(initialItems);
  const [selection, setSelection] = useState<PassportCard[]>(() => initialCard ? [initialCard] : initialItems.slice(0, 1));
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState('');
  const [faces, setFaces] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [cursor, setCursor] = useState<string | null>(null);
  const [total, setTotal] = useState(initialItems.length);
  const [reload, setReload] = useState(0);
  const live = useRef(true);
  const baseQuery = useMemo(() => {
    const next = new URLSearchParams(query);
    // Timeline's month stack fetches are independent. Read the same filtered set
    // without changing the owning page's URL, opened months, or canvas.
    next.delete('timeline'); next.delete('cursor'); next.set('group', 'none'); next.set('limit', '200');
    return next.toString();
  }, [query]);
  useEffect(() => {
    live.current = true;
    let current = true;
    setBusy(true); setError('');
    collectionRequest<CollectionResult>(new URLSearchParams(baseQuery)).then(result => {
      if (!current) return;
      setItems(result.items); setCursor(result.nextCursor); setTotal(result.totalMatching);
      setSelection(previous => previous.length ? previous : result.items.slice(0, 1));
    }).catch(e => { if (current) setError(e.message || 'Could not open your desk.'); })
      .finally(() => { if (current) setBusy(false); });
    return () => { current = false; live.current = false; };
  }, [baseQuery, reload]);
  async function more() {
    if (!cursor || busy) return;
    setBusy(true); setError('');
    try {
      const next = new URLSearchParams(baseQuery); next.set('cursor', cursor);
      const result = await collectionRequest<CollectionResult>(next);
      if (!live.current) return;
      setItems(previous => Array.from(new Map([...previous, ...result.items].map(card => [card.key, card])).values()));
      setCursor(result.nextCursor); setTotal(result.totalMatching);
    } catch (e) { if (live.current) setError((e as Error).message); }
    finally { if (live.current) setBusy(false); }
  }
  const filtered = items.filter(card => (!kind || card.subject_type === kind) &&
    `${card.title} ${card.issuer} ${card.subject?.subtitle || ''}`.toLowerCase().includes(search.trim().toLowerCase()));
  const kinds = Array.from(new Set(items.map(card => card.subject_type))).sort();
  const toggle = (card: PassportCard) => setSelection(previous => previous.some(c => c.key === card.key)
    ? previous.filter(c => c.key !== card.key) : previous.length < 2 ? [...previous, card] : previous);
  return <AdaptiveWorkspaceDialog title="Collector’s Desk" onClose={onClose} className="collectors-desk"
    primary={<>
      <p className="adaptive-workspace__muted">{selection.length === 2 ? 'Two records, side by side.' : 'Choose a passport. Add another to compare.'}</p>
      <div className="collectors-desk__artwork" style={{ '--desk-selection-count': Math.max(1, selection.length) } as React.CSSProperties}>
        {selection.map(card => <article key={card.key} aria-label={`Selected passport: ${card.title}`}>
          <PassportCardArtwork card={card} staticOutput quiet back={Boolean(faces[card.key!])} loadBuildDetails={false} imageAlt={card.title} />
        </article>)}
      </div>
      {!selection.length && <p>No passports selected. Choose up to two from the collection.</p>}
    </>}
    secondary={<>
      <p>Explore the current collection without changing its saved arrangement.</p>
      {selection.length > 0 && <div className="collectors-desk__records">{selection.map(card => <article key={card.key} aria-label={`Record: ${card.title}`}>
        <h3>{card.title}</h3>
        <div className="adaptive-workspace__actions">
          <button type="button" aria-pressed={Boolean(faces[card.key!])} onClick={() => setFaces(previous => ({ ...previous, [card.key!]: !previous[card.key!] }))}>Turn {card.title} {faces[card.key!] ? 'to front' : 'over'}</button>
          <button type="button" onClick={() => toggle(card)}>Remove {card.title} from desk</button>
        </div>
        <dl>
          <div><dt>Type</dt><dd>{card.subject_type}</dd></div>
          <div><dt>Issuer</dt><dd>{card.issuer}</dd></div>
          <div><dt>Collected</dt><dd>{timelineCollectedLabel(card.earned_at)}</dd></div>
          {card.evidence && <div><dt>Evidence</dt><dd>{cardEvidenceLabel(card.evidence)}</dd></div>}
          {card.ordinal != null && <div><dt>Edition</dt><dd>#{card.ordinal}</dd></div>}
        </dl>
      </article>)}</div>}
      <label>Find a passport on this desk<input type="search" value={search} onChange={e => setSearch(e.target.value)} /></label>
      <label>Passport type<select value={kind} onChange={e => setKind(e.target.value)}><option value="">All types</option>{kinds.map(type => <option key={type} value={type}>{type}</option>)}</select></label>
      <p role="status">{busy ? 'Opening passports…' : `${selection.length} of 2 selected · ${items.length} of ${total} loaded`}</p>
      {error && <div role="alert"><p>{error}</p><button type="button" onClick={() => setReload(v => v + 1)}>Try again</button></div>}
      <div className="adaptive-workspace__records" aria-label="Choose passports to compare">
        {filtered.map(card => {
          const selected = selection.some(c => c.key === card.key);
          return <button key={card.key} type="button" aria-pressed={selected} disabled={!selected && selection.length === 2} onClick={() => toggle(card)}>
            <strong>{card.title}</strong><small>{card.issuer} · {timelineCollectedLabel(card.earned_at)}</small>
          </button>;
        })}
      </div>
      {!busy && !filtered.length && <p>No passports match these filters.</p>}
      {cursor && <button type="button" disabled={busy} onClick={() => { void more(); }}>Load more passports</button>}
    </>} />;
}
